import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiJson } from '../apiClient';
import { FACTORY_RAW_MATERIAL_CATEGORIES, factoryCategoryShort } from '../constants/factoryStockCategories';

export function useFactoryRawMaterialCategories(enabled = true) {
  const [all, setAll] = useState(FACTORY_RAW_MATERIAL_CATEGORIES);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiJson('/brick/raw-material-categories');
      const list = Array.isArray(data.all) && data.all.length ? data.all : FACTORY_RAW_MATERIAL_CATEGORIES;
      setAll(list);
    } catch (e) {
      setAll(FACTORY_RAW_MATERIAL_CATEGORIES);
      setError(e.message || 'Could not load categories.');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    void refresh();
    return undefined;
  }, [refresh, enabled]);

  const selectOptions = useMemo(
    () => [
      { value: '', label: 'Select category…' },
      ...all.map((c) => ({ value: c, label: factoryCategoryShort(c) })),
    ],
    [all]
  );

  const updateCategory = useCallback(
    async (from, name) => {
      const data = await apiJson('/brick/raw-material-categories', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, name }),
      });
      await refresh();
      return data;
    },
    [refresh]
  );

  const deleteCategory = useCallback(
    async (name) => {
      const data = await apiJson('/brick/raw-material-categories', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      await refresh();
      return data;
    },
    [refresh]
  );

  return { categories: all, selectOptions, loading, error, refresh, updateCategory, deleteCategory };
}
